import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { User, KeyRound, ShieldCheck, QrCode, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  useProfile,
  useUpdateProfile,
  useChangePassword,
  useMfaSetup,
  useVerifyMfa,
  useResetSelfMfa,
  type MfaSetupData,
} from '@/api/hooks/useSettings';

// ── MFA Setup Modal ───────────────────────────────────────────────────────────

function MfaSetupModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const setupMutation = useMfaSetup();
  const verifyMutation = useVerifyMfa();
  const [setupData, setSetupData] = useState<MfaSetupData | null>(null);
  const [code, setCode] = useState('');

  useEffect(() => {
    setupMutation.mutate(undefined, {
      onSuccess: (d) => setSetupData(d),
      onError: () => toast.error('Failed to start MFA setup'),
    });
  }, []);

  function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    verifyMutation.mutate(code, {
      onSuccess: () => {
        toast.success('MFA enabled successfully');
        onSuccess();
      },
      onError: (err: any) => toast.error(err?.response?.data?.errors?.[0]?.message ?? 'Invalid code'),
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-xl space-y-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-cyan/10">
            <Smartphone className="h-5 w-5 text-accent-cyan" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Set Up Two-Factor Authentication</h2>
            <p className="text-xs text-text-secondary">Scan the QR code with your authenticator app</p>
          </div>
        </div>

        {setupMutation.isPending && (
          <div className="space-y-3">
            <Skeleton className="h-48 w-48 mx-auto rounded-lg" />
            <Skeleton className="h-4 w-3/4 mx-auto" />
          </div>
        )}

        {setupData && (
          <>
            <div className="flex flex-col items-center gap-3">
              {/* QR rendered locally (inline SVG) from otpauthUrl — secret stays in-browser */}
              <QRCodeSVG value={setupData.otpauthUrl} size={192} marginSize={2} title="MFA QR Code" className="rounded-lg border border-border-subtle" />
              <p className="text-xs text-text-secondary text-center">
                Can't scan? Enter this key manually:
              </p>
              <code className="rounded bg-bg-surface-2 px-3 py-1.5 text-xs font-mono text-text-primary tracking-widest break-all text-center">
                {setupData.secret}
              </code>
            </div>

            <form onSubmit={handleVerify} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Enter the 6-digit code from your app</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-lg tracking-widest font-mono"
                  maxLength={6}
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={code.length !== 6 || verifyMutation.isPending} className="flex-1">
                  {verifyMutation.isPending ? 'Verifying…' : 'Enable MFA'}
                </Button>
                <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ── MFA Re-enroll Modal ───────────────────────────────────────────────────────

function MfaReenrollModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const resetSelfMutation = useResetSelfMfa();
  const [password, setPassword] = useState('');
  const [cleared, setCleared] = useState(false);

  function handleReset(e: React.FormEvent) {
    e.preventDefault();
    resetSelfMutation.mutate(password, {
      onSuccess: () => setCleared(true),
      onError: (err: any) => toast.error(err?.response?.data?.errors?.[0]?.message ?? 'Incorrect password'),
    });
  }

  if (cleared) {
    return <MfaSetupModal onClose={onClose} onSuccess={onSuccess} />;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-sm rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-xl space-y-5">
        <h2 className="text-lg font-semibold text-text-primary">Re-enroll Authenticator</h2>
        <p className="text-sm text-text-secondary">
          This will remove your current authenticator. Enter your password to continue.
        </p>
        <form onSubmit={handleReset} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Current Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={!password || resetSelfMutation.isPending} className="flex-1">
              {resetSelfMutation.isPending ? 'Confirming…' : 'Continue'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── ProfilePage ───────────────────────────────────────────────────────────────

export function ProfilePage() {
  const { data: profile, isLoading } = useProfile();
  const updateMutation = useUpdateProfile();
  const passwordMutation = useChangePassword();

  const [displayName, setDisplayName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [mfaModal, setMfaModal] = useState<'setup' | 'reenroll' | null>(null);

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName ?? profile.display_name ?? '');
      setFirstName(profile.firstName ?? profile.first_name ?? '');
      setLastName(profile.lastName ?? profile.last_name ?? '');
    }
  }, [profile]);

  const initials = (displayName || 'U').split(' ').map((w: string) => w[0]).join('').substring(0, 2).toUpperCase();
  const mfaEnabled: boolean = (profile as any)?.mfaEnabled ?? false;
  const isSsoUser: boolean = !!((profile as any)?.auth_method?.startsWith('sso'));

  function handleProfileSave(e: React.FormEvent) {
    e.preventDefault();
    updateMutation.mutate(
      { displayName, firstName, lastName },
      {
        onSuccess: () => toast.success('Profile updated'),
        onError: () => toast.error('Failed to update profile'),
      },
    );
  }

  function handlePasswordChange(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 12) {
      toast.error('Password must be at least 12 characters.');
      return;
    }
    passwordMutation.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          toast.success('Password changed');
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        },
        onError: () => toast.error('Failed to change password'),
      },
    );
  }

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-32 w-full" /><Skeleton className="h-48 w-full" /></div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Profile</h1>
        <p className="mt-1 text-sm text-text-secondary">Your account information</p>
      </div>

      {/* Avatar + email */}
      <div className="flex items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-cyan/20 text-accent-cyan text-xl font-bold">
          {initials}
        </div>
        <div>
          <p className="font-medium text-text-primary">{displayName || 'No name set'}</p>
          <p className="text-sm text-text-secondary">{profile?.email}</p>
        </div>
      </div>

      {/* Profile form */}
      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><User className="h-4 w-4" /> Personal Information</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleProfileSave} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Display Name</Label>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="How you appear in the app" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>First Name</Label>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Last Name</Label>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input value={profile?.email ?? ''} readOnly className="opacity-60 cursor-not-allowed" />
              <p className="text-xs text-text-secondary">Email cannot be changed here. Contact your admin.</p>
            </div>
            <Button type="submit" disabled={updateMutation.isPending}>Save Changes</Button>
          </form>
        </CardContent>
      </Card>

      {/* Password form — hidden for SSO users */}
      {isSsoUser ? (
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-text-secondary">
              Password and MFA are managed by your identity provider. Contact your organization administrator if you need access help.
            </p>
          </CardContent>
        </Card>
      ) : (
      <>
      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><KeyRound className="h-4 w-4" /> Change Password</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Current Password</Label>
              <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label>New Password</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={12} />
              <p className="text-xs text-text-secondary">Must be at least 12 characters.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Confirm New Password</Label>
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
            </div>
            <Button type="submit" disabled={passwordMutation.isPending || !currentPassword || !newPassword}>
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Security / MFA */}
      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Security</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <QrCode className="h-4 w-4 text-text-secondary" />
                <span className="text-sm font-medium text-text-primary">Two-Factor Authentication</span>
                {mfaEnabled ? (
                  <Badge variant="emerald">Enabled</Badge>
                ) : (
                  <Badge variant="rose">Not Enabled</Badge>
                )}
              </div>
              {mfaEnabled ? (
                <p className="text-xs text-text-secondary pl-6">
                  MFA is active. Your account requires an authenticator code at login.
                  {(profile as any)?.updatedAt && (
                    <> Configured {format(new Date((profile as any).updatedAt), 'dd MMM yyyy')}.</>
                  )}
                </p>
              ) : (
                <p className="text-xs text-text-secondary pl-6">
                  Protect your account with an authenticator app like Google Authenticator or Authy.
                </p>
              )}
            </div>
          </div>

          {mfaEnabled ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setMfaModal('reenroll')}>
                Re-enroll Authenticator
              </Button>
            </div>
          ) : (
            <Button size="sm" onClick={() => setMfaModal('setup')}>
              Enable MFA
            </Button>
          )}
        </CardContent>
      </Card>
      </>
      )}

      {/* Modals */}
      {mfaModal === 'setup' && (
        <MfaSetupModal
          onClose={() => setMfaModal(null)}
          onSuccess={() => setMfaModal(null)}
        />
      )}
      {mfaModal === 'reenroll' && (
        <MfaReenrollModal
          onClose={() => setMfaModal(null)}
          onSuccess={() => setMfaModal(null)}
        />
      )}
    </div>
  );
}
